import { OffsetRange } from '../core/offsetRange.js';
import {
	DefaultLinesDiffComputer,
	ILinesDiffComputerOptions,
} from '../defaultLinesDiffComputer/defaultLinesDiffComputer.js';
import {
	LinesDiff,
	DetailedLineRangeMapping,
	LineRange,
	RangeMapping,
	MovedText,
} from '../defaultLinesDiffComputer/rangeMapping.js';
import { Range } from '../defaultLinesDiffComputer/linesSliceCharSequence.js';
import { initWasm, getWasm } from '../wasm/loader.js';
import type { WasmDiffOptions, WasmLinesDiff } from '../wasm/types.js';

import { OffsetRangeMapping } from './offsetRange.js';
import { StringEdit, StringReplacement } from './stringEdit.js';
import {
	AnnotatedStringEdit,
	AnnotatedStringReplacement,
	DiffAnnotation,
} from './annotatedStringEdit.js';
import { Move } from './move.js';
import { DiffOptions, DiffResult, IDiffComputer } from './diffResult.js';

// ============================================================================
// Public factory
// ============================================================================

export interface CreateDiffComputerOptions {
	/**
	 * Use the WASM-backed implementation. Loads the WASM module on first creation.
	 * Default: false (pure-TS implementation).
	 */
	useWasm?: boolean;

	/**
	 * Algorithm hint passed to the computer.
	 */
	algorithm?: 'myers' | 'dynamic-programming';
}

export async function createDiffComputer(
	options: CreateDiffComputerOptions = {},
): Promise<IDiffComputer> {
	if (options.useWasm) {
		await initWasm();
		return new _WasmDiffComputer(options.algorithm);
	}
	return new _TsDiffComputer(options.algorithm);
}

// ============================================================================
// TS-backed computer
// ============================================================================

class _TsDiffComputer implements IDiffComputer {
	private readonly _impl = new DefaultLinesDiffComputer();

	constructor(private readonly _algorithm: 'myers' | 'dynamic-programming' | undefined) { }

	computeDiff(original: string, modified: string, options?: DiffOptions): DiffResult {
		const originalLines = original.split('\n');
		const modifiedLines = modified.split('\n');
		const internal: ILinesDiffComputerOptions = {
			maxComputationTimeMs: options?.maxComputationTimeMs ?? 0,
			ignoreTrimWhitespace: options?.ignoreTrimWhitespace ?? false,
			computeMoves: options?.computeMoves ?? false,
			extendToSubwords: options?.extendToSubwords ?? false,
		};
		const linesDiff = this._impl.computeDiff(originalLines, modifiedLines, internal);
		void this._algorithm; // currently a hint; auto-selection lives inside DefaultLinesDiffComputer
		return _adaptLinesDiff(linesDiff, original, modified, originalLines, modifiedLines);
	}
}

// ============================================================================
// WASM-backed computer
// ============================================================================

class _WasmDiffComputer implements IDiffComputer {
	constructor(private readonly _algorithm: 'myers' | 'dynamic-programming' | undefined) { }

	computeDiff(original: string, modified: string, options?: DiffOptions): DiffResult {
		const originalLines = original.split('\n');
		const modifiedLines = modified.split('\n');
		const wasmOptions: WasmDiffOptions = {};
		if (options?.maxComputationTimeMs !== undefined) wasmOptions.maxComputationTimeMs = options.maxComputationTimeMs;
		if (options?.ignoreTrimWhitespace !== undefined) wasmOptions.ignoreTrimWhitespace = options.ignoreTrimWhitespace;
		if (options?.computeMoves !== undefined) wasmOptions.computeMoves = options.computeMoves;
		if (options?.extendToSubwords !== undefined) wasmOptions.extendToSubwords = options.extendToSubwords;
		const wasm = getWasm();
		const wasmResult: WasmLinesDiff = wasm.computeDiffLines(originalLines, modifiedLines, wasmOptions);
		void this._algorithm;
		const linesDiff = _wasmToLinesDiff(wasmResult);
		return _adaptLinesDiff(linesDiff, original, modified, originalLines, modifiedLines);
	}
}

function _wasmToLinesDiff(wasmResult: WasmLinesDiff): LinesDiff {
	const changes = wasmResult.changes.map((c) => {
		const inner = c.innerChanges?.map((ic) =>
			new RangeMapping(
				new Range(ic.originalStartLine, ic.originalStartColumn, ic.originalEndLine, ic.originalEndColumn),
				new Range(ic.modifiedStartLine, ic.modifiedStartColumn, ic.modifiedEndLine, ic.modifiedEndColumn),
			),
		);
		return new DetailedLineRangeMapping(
			new LineRange(c.original.startLineNumber, c.original.endLineNumberExclusive),
			new LineRange(c.modified.startLineNumber, c.modified.endLineNumberExclusive),
			inner,
		);
	});
	return new LinesDiff(changes, [], wasmResult.hitTimeout);
}

// ============================================================================
// LinesDiff -> DiffResult adapter
// ============================================================================

/** @internal — exported for the worker-side client. */
export function _adaptLinesDiff(
	linesDiff: LinesDiff,
	original: string,
	modified: string,
	originalLines: string[],
	modifiedLines: string[],
): DiffResult {
	const originalLineStarts = _computeLineStarts(originalLines);
	const modifiedLineStarts = _computeLineStarts(modifiedLines);

	const replacements: AnnotatedStringReplacement<DiffAnnotation>[] = [];
	for (const change of linesDiff.changes) {
		_appendChangeReplacements(
			change, original, modified,
			originalLines, modifiedLines,
			originalLineStarts, modifiedLineStarts,
			replacements,
		);
	}

	const edits = AnnotatedStringEdit.of(replacements);

	const moves: Move[] = linesDiff.moves.map(m =>
		_adaptMove(m, original, modified, originalLines, modifiedLines, originalLineStarts, modifiedLineStarts),
	);

	return new DiffResult(edits, moves, linesDiff.hitTimeout);
}

function _appendChangeReplacements(
	change: DetailedLineRangeMapping,
	original: string,
	modified: string,
	originalLines: string[],
	modifiedLines: string[],
	originalLineStarts: number[],
	modifiedLineStarts: number[],
	out: AnnotatedStringReplacement<DiffAnnotation>[],
): void {
	if (change.innerChanges && change.innerChanges.length > 0) {
		for (const ic of change.innerChanges) {
			const origRange = _rangeToOffsetRange(ic.originalRange, original, originalLines, originalLineStarts);
			const modRange = _rangeToOffsetRange(ic.modifiedRange, modified, modifiedLines, modifiedLineStarts);
			const newText = modified.substring(modRange.start, modRange.endExclusive);
			out.push(new AnnotatedStringReplacement(origRange, newText, DiffAnnotation.change));
		}
		return;
	}

	// Fallback: no inner detail — replace the whole line range.
	const origRange = _lineRangeToOffsetRange(
		change.original.startLineNumber, change.original.endLineNumberExclusive,
		original, originalLineStarts,
	);
	const modRange = _lineRangeToOffsetRange(
		change.modified.startLineNumber, change.modified.endLineNumberExclusive,
		modified, modifiedLineStarts,
	);
	const newText = modified.substring(modRange.start, modRange.endExclusive);
	out.push(new AnnotatedStringReplacement(origRange, newText, DiffAnnotation.change));
}

function _adaptMove(
	move: MovedText,
	original: string,
	modified: string,
	originalLines: string[],
	modifiedLines: string[],
	originalLineStarts: number[],
	modifiedLineStarts: number[],
): Move {
	const origRange = _lineRangeToOffsetRange(
		move.lineRangeMapping.original.startLineNumber,
		move.lineRangeMapping.original.endLineNumberExclusive,
		original, originalLineStarts,
	);
	const modRange = _lineRangeToOffsetRange(
		move.lineRangeMapping.modified.startLineNumber,
		move.lineRangeMapping.modified.endLineNumberExclusive,
		modified, modifiedLineStarts,
	);

	// Build inner edit with offsets local to the moved slices.
	const inner: StringReplacement[] = [];
	for (const change of move.changes) {
		if (change.innerChanges && change.innerChanges.length > 0) {
			for (const ic of change.innerChanges) {
				const globalOrig = _rangeToOffsetRange(ic.originalRange, original, originalLines, originalLineStarts);
				const globalMod = _rangeToOffsetRange(ic.modifiedRange, modified, modifiedLines, modifiedLineStarts);
				inner.push(new StringReplacement(
					new OffsetRange(globalOrig.start - origRange.start, globalOrig.endExclusive - origRange.start),
					modified.substring(globalMod.start, globalMod.endExclusive),
				));
			}
		} else {
			const globalOrig = _lineRangeToOffsetRange(
				change.original.startLineNumber, change.original.endLineNumberExclusive,
				original, originalLineStarts,
			);
			const globalMod = _lineRangeToOffsetRange(
				change.modified.startLineNumber, change.modified.endLineNumberExclusive,
				modified, modifiedLineStarts,
			);
			inner.push(new StringReplacement(
				new OffsetRange(globalOrig.start - origRange.start, globalOrig.endExclusive - origRange.start),
				modified.substring(globalMod.start, globalMod.endExclusive),
			));
		}
	}

	return new Move(new OffsetRangeMapping(origRange, modRange), StringEdit.of(inner));
}

// ----- offset/line helpers -----

function _computeLineStarts(lines: string[]): number[] {
	// Offsets correspond to `lines.join('\n')`, which equals the input string
	// when the input was split by `\n`.
	const starts: number[] = new Array(lines.length + 1);
	starts[0] = 0;
	for (let i = 0; i < lines.length; i++) {
		starts[i + 1] = starts[i] + lines[i].length + 1; // +1 for the '\n'
	}
	// `starts[lines.length]` is one past the synthetic trailing newline; the
	// real text ends one earlier when there is no trailing newline (which is
	// what `split('\n')` produces for non-newline-terminated input).
	return starts;
}

function _lineRangeToOffsetRange(
	startLineNumber: number,
	endLineNumberExclusive: number,
	text: string,
	lineStarts: number[],
): OffsetRange {
	const start = _lineColToOffset(startLineNumber, 1, text, lineStarts);
	const end = _lineColToOffset(endLineNumberExclusive, 1, text, lineStarts);
	return new OffsetRange(start, end);
}

function _rangeToOffsetRange(
	range: Range,
	text: string,
	lines: string[],
	lineStarts: number[],
): OffsetRange {
	void lines;
	const start = _lineColToOffset(range.startLineNumber, range.startColumn, text, lineStarts);
	const end = _lineColToOffset(range.endLineNumber, range.endColumn, text, lineStarts);
	return new OffsetRange(start, end);
}

function _lineColToOffset(
	lineNumber: number,
	column: number,
	text: string,
	lineStarts: number[],
): number {
	// `lineNumber` and `column` are 1-based; `endLineNumberExclusive` can be
	// `lines.length + 1` (one past the last line).
	if (lineNumber <= 0) { return 0; }
	if (lineNumber > lineStarts.length) {
		return text.length;
	}
	const base = lineStarts[lineNumber - 1];
	const offset = base + (column - 1);
	return Math.min(Math.max(offset, 0), text.length);
}
